import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import handleAsyncThunk from "../handleAsyncThunk";
import addressApi from "../../service/addressApi";
import { showSuccessSnackbar, showErrorSnackbar } from "./snackbarSlice";

export const createNewAddress = createAsyncThunk(
  "address/createNewAddress",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await addressApi.addNewAddress(data);
      if (response.status === 201) {
        dispatch(showSuccessSnackbar({ title: "Address created successfully" }));
        return response?.data;
      } else {
        dispatch(showErrorSnackbar({ title: response?.message || "Something went wrong" }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ title: error?.message }));
      return rejectWithValue(error.message);
    }
  }
);

export const getAddresses = createAsyncThunk(
  "address/getAddresses",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const queryString = data?.q ? `?q=${data.q}` : "";
      const response = await addressApi.getAddress(queryString);
      if (response.status === 200) {
        return response?.data;
      } else {
        dispatch(showErrorSnackbar({ title: response?.message || "Something went wrong" }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ title: error?.message }));
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSearchedAddresses = createAsyncThunk(
  "address/fetchSearchedAddresses",
  async (queryObj, { rejectWithValue, dispatch }) => {
    try {
      console.log("Starting fetchSearchedAddresses with query:", queryObj);
      const queryString = queryObj
        ? `?q=${Object.entries(queryObj)
            .map(([key, value]) => `${key}:${value}`)
            .join(",")}`
        : "";
      console.log("Constructed query string:", queryString);
      const response = await addressApi.searchAddress(queryString);
      console.log("API response received:", {
        status: response.status,
        data: response.data,
        headers: response.headers,
      });
      if (response.status === 200) {
        console.log("fetchSearchedAddresses successful, data:", response.data);
        return response?.data;
      } else {
        console.error(
          "fetchSearchedAddresses failed with status:",
          response.status,
          "Message:",
          response?.message
        );
        dispatch(showErrorSnackbar({ title: response?.message || "Failed to fetch searched addresses" }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      console.error("fetchSearchedAddresses error:", {
        message: error.message,
        stack: error.stack,
      });
      dispatch(showErrorSnackbar({ title: error?.message || "Error searching addresses" }));
      return rejectWithValue(error.message);
    }
  }
);

export const getAddressById = createAsyncThunk(
  "address/getAddressById",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await addressApi.getAddressById(data);
      if (response.status === 200) {
        return response?.data;
      } else {
        dispatch(showErrorSnackbar({ title: response?.message || "Something went wrong" }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ title: error?.message }));
      return rejectWithValue(error.message);
    }
  }
);

export const updateAddress = createAsyncThunk(
  "address/updateAddress",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await addressApi.updateAddress(data);
      if (response.status === 200) {
        dispatch(showSuccessSnackbar({ title: "Address updated successfully" }));
        return response?.data;
      } else {
        dispatch(showErrorSnackbar({ title: response?.message || "Something went wrong" }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ title: error?.message }));
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAddress = createAsyncThunk(
  "address/deleteAddress",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await addressApi.deleteAddress(data);
      if (response.status === 204) {
        dispatch(showSuccessSnackbar({ title: "Address deleted successfully" }));
        return response?.data;
      } else {
        dispatch(showErrorSnackbar({ title: response?.message || "Something went wrong" }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ title: error?.message }));
      return rejectWithValue(error.message);
    }
  }
);

const addressSlice = createSlice({
  name: "address",
  initialState: {
    data: null,
    status: null,
    error: null,
    isLoading: false,
    addresses: [],
    addressList: [],
    addressListLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    handleAsyncThunk(builder, createNewAddress, "data", "error", "addressLoading");
    handleAsyncThunk(builder, getAddresses, "addresses", "error", "addressesListLoading");
    handleAsyncThunk(builder, getAddressById, "addressById", "error", "addressByIdLoading");
    handleAsyncThunk(builder, deleteAddress, "data", "error", "deleteAddressLoading");
    handleAsyncThunk(builder, updateAddress, "data", "error", "updateAddressLoading");
    handleAsyncThunk(builder, fetchSearchedAddresses, "addressList", "error", "addressListLoading");
  },
});

export default addressSlice.reducer;